// Core imports
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

// Component imports
import { Col, Row } from 'antd';
import LoginForm from '../components/Forms/LoginForm';
import HelviaCard from '../components/UI/HelviaCard';
import OidcAuthorizationCompletionLoader from '../components/UI/OidcAuthorizationCompletionLoader';

// Misc imports
import { NAV_DASHBOARD } from '../constants/navigation';
import { IS_LOGIN_SCREEN_REDIRECT_DISABLED } from '../constants/env';
import { useFetchOrganizationPublicDataQuery } from '../store/organization/organizationApi';
import replaceUrlParams from '../utils/string/replaceUrlParams';
import is from '../utils/is';
import LogoFull from '../assets/logo-full.svg';

const LoginScreen = ({ injectedOrganizationData }) => {
  const { currentUser, organizationId } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();
  const [hasFinishedWithOidc, setHasFinishedWithOidc] = useState(false);

  const isAuthenticated = !!currentUser;
  const urlSearchParams = new URLSearchParams(location.search);
  const orgIdParam = urlSearchParams.get('orgId');
  const orgId = injectedOrganizationData?.id || orgIdParam;
  const isDisplayOnly = injectedOrganizationData != null;

  const { data: _orgData, isLoading: isLoadingOrgData } = useFetchOrganizationPublicDataQuery(
    { orgId: orgId, urlParams: { includeLoginSettings: true } },
    { skip: !orgId }
  );
  const orgData = useMemo(() => {
    return {
      ..._orgData,
      ...injectedOrganizationData
    };
  }, [_orgData, injectedOrganizationData]);

  // This check is used to prevent redirect conflicts  when running the console through an MS Teams tab
  useEffect(() => {
    if (isAuthenticated && !is.inMicrosoftTeams() && !isDisplayOnly && !IS_LOGIN_SCREEN_REDIRECT_DISABLED) {
      const next = urlSearchParams.get('next');
      if (next) {
        navigate(next);
      } else {
        navigate(replaceUrlParams(NAV_DASHBOARD, { orgId: organizationId }));
      }
    }
  }, [isAuthenticated, isDisplayOnly]);

  if ((isLoadingOrgData || (orgId && !hasFinishedWithOidc)) && !isDisplayOnly) {
    return (
      <OidcAuthorizationCompletionLoader
        organizationId={orgId}
        loginSettings={orgData?.loginSettings}
        onAuthorizationCompletion={() => setHasFinishedWithOidc(true)}
      />
    );
  }

  return (
    <HelviaCard
      leftSideContent={<LoginForm loginSettings={orgData?.loginSettings} organizationId={orgId} />}
      rightSideContent={
        <Row justify="center" align="middle" className="h-100">
          <Col>
            <img src={LogoFull} alt="logo" width={240} />
          </Col>
        </Row>
      }
      org={orgData}
    />
  );
};

LoginScreen.propTypes = {
  /**
   * This is used to inject organization data into the component.
   * This is useful for testing and display-only purposes.
   *
   * Normally, when user navigates to the login screen, the organization data is fetched from the server
   * and there will be NO injected data.
   */
  injectedOrganizationData: PropTypes.object
};

export default LoginScreen;
